import {
  addPlayerCampaign,
  assignLicenceToPlayer,
  deletePlayerCampaign,
  getPlayerCampaign,
  updatePlayer,
} from "@/sideEffects/api";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormSchemaType, PlayerMetaForm } from "../form/PlayerMetaForm";
import { useTranslation } from "react-i18next";
import { PlayerDetails } from "@/lib/types";
import { showSuccess, showApiError } from "@/lib/toast";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { LoadingSmall } from "@/components/LoadingSmall";

interface Props {
  playerDetails: PlayerDetails;
}

export const UpdatePlayerMetaForm: React.FC<Props> = ({ playerDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const { data: playerCampaignData, isLoading: isPlayerCampaignLoading } = useQuery({
    queryFn: () => getPlayerCampaign(playerDetails.id, String(activeFirm?.firmGuid)),
    queryKey: ["getPlayerCampaign", playerDetails.id],
  });

  const existingPlayerCampaigns = playerCampaignData?.data?.result || [];

  const updateMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      await updatePlayer({
        id: playerDetails.id,
        name: values.name,
        screenSizeId: values.screenSizeId,
        timeZone: values.timeZone,
        channelId: values.channelId,
        contentGroupId: values.contentGroupId,
        contentId: values.contentId,
        defaultContentId: values.defaultContentId,
        firmGuid: String(activeFirm?.firmGuid),
        areaId: values.areaId,
        latitude: values.latitude,
        longitude: values.longitude,
        healthSequence: values.healthSequence,
      });

      if (values.firmLicenceintId) {
        await assignLicenceToPlayer({
          firmGuid: String(activeFirm?.firmGuid),
          playerId: playerDetails.id,
          firmLicenceId: values.firmLicenceintId,
        });
      }

      const isSameCampaignIds =
        existingPlayerCampaigns.length === values.campaignIds?.length &&
        existingPlayerCampaigns.every((campaign) => values.campaignIds?.includes(campaign.campaignintId));

      if (isSameCampaignIds) {
        return Promise.resolve();
      }

      for (const existing of existingPlayerCampaigns) {
        await deletePlayerCampaign(existing.id, String(activeFirm?.firmGuid));
      }

      if (values.campaignIds) {
        for (const campaignId of values.campaignIds) {
          await addPlayerCampaign({
            playerId: playerDetails.intId,
            campaignId,
            firmGuid: String(activeFirm?.firmGuid),
          });
        }
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      showSuccess(t("common.updatedSuccess"));
      setTimeout(() => {
        document.location.reload();
      }, 1000);
    },
    onError: showApiError,
  });

  if (isPlayerCampaignLoading) return <LoadingSmall />;

  return (
    <PlayerMetaForm
      key={JSON.stringify(playerDetails)}
      buttonText={t("common.update")}
      onSubmit={(values) => updateMutation.mutate(values)}
      isLoading={updateMutation.isPending || isPlayerCampaignLoading}
      defaultValues={{
        name: playerDetails.name,
        screenSizeId: playerDetails.screenSizeintId,
        timeZone: playerDetails.timezone,
        firmLicenceintId: playerDetails.firmLicenceintId,
        channelId: playerDetails.channelintId,
        contentGroupId: playerDetails.contentGroupintId,
        contentId: playerDetails.contentintId,
        defaultContentId: playerDetails.defaultContentintId,
        areaId: playerDetails.areaintId,
        campaignIds: existingPlayerCampaigns.map((r) => r.campaignintId),
        latitude: Number(playerDetails?.latitude) || 0,
        longitude: Number(playerDetails?.longitude) || 0,
        healthSequence: playerDetails.healthSequence || 2,
      }}
    />
  );
};
